import React, { useState, useEffect, useMemo } from 'react'
import debounce from 'lodash/debounce'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  MeetingRoom as RoomIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  History as HistoryIcon,
  Message as MessageIcon,
} from '@mui/icons-material'

import ProfileAvatar from '../ProfileAvatar'
import Parse from 'parse'
import { adminService } from '../../services/AdminService'
import {
  deleteUsersSequentially,
  describeSingleDeleteFailure,
  summarizeBulkDelete,
} from './userDeletionMessages'
import {
  buildAdminUserListRequestOptions,
  type CompanyPick,
  type UserListFilterState,
} from '../../utils/adminUserListClient'

interface User {
  id: string
  username: string
  email: string
  role: string
  isPlatformAdmin: boolean
  createdAt: string
  updatedAt: string
  /** Profile pattern style and photo; absent for a user without a Profile. */
  avatar?: string
  customAvatarUrl?: string
}

interface DeletionPreview {
  user: {
    id: string
    username: string
    email: string
    role: string
    isPlatformAdmin: boolean
    createdAt: string
  }
  companyAssignments: Array<{
    companyId: string
    companyName: string
    role: string
    isActive: boolean
    joinedAt: string
  }>
  rooms: Array<{
    id: string
    name: string
    description: string
    createdAt: string
  }>
  profiles: Array<{
    id: string
    username: string
    email: string
    createdAt: string
  }>
  billingData: Array<{
    id: string
    billingTier: string
    subscriptionPlan: string
    stripeCustomerId: string
    createdAt: string
  }>
  otherData: {
    sessions: number
    activities: number
    messages: number
  }
  warnings: string[]
  summary: {
    totalCompanyAssignments: number
    totalRooms: number
    totalProfiles: number
    totalBillingAccounts: number
    totalSessions: number
    totalActivities: number
    totalMessages: number
  }
}

const UserManagementCard: React.FC = () => {
  const theme = useTheme()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedUserForDeletion, setSelectedUserForDeletion] = useState<User | null>(null)
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [deletionPreview, setDeletionPreview] = useState<DeletionPreview | null>(null)
  const [deletionReason, setDeletionReason] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  })

  const [roleFilter, setRoleFilter] = useState('all')
  const [platformAdminFilter, setPlatformAdminFilter] = useState<'any' | 'yes' | 'no'>('any')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')
  const [companyFilter, setCompanyFilter] = useState<CompanyPick | null>(null)
  const [noCompany, setNoCompany] = useState(false)
  const [companyOptions, setCompanyOptions] = useState<CompanyPick[]>([])

  const loadCompanies = useMemo(
    () =>
      debounce(async (q: string) => {
        try {
          const res = await adminService.getCompanies(30, 0, q.trim() || undefined)
          if (res.success) {
            setCompanyOptions(res.companies.map((c) => ({ id: c.id, name: c.name })))
          }
        } catch (e) {
          console.error('Error loading companies for filter:', e)
        }
      }, 300),
    []
  )

  useEffect(() => () => loadCompanies.cancel(), [loadCompanies])

  const snapshotFilters = (overrides: Partial<UserListFilterState> = {}): UserListFilterState => ({
    searchTerm,
    roleFilter,
    platformAdminFilter,
    createdFrom,
    createdTo,
    companyFilter,
    noCompany,
    ...overrides,
  })

  const fetchPage = async (page: number, limit: number, overrides: Partial<UserListFilterState> = {}) => {
    setLoading(true)
    try {
      const currentUser = Parse.User.current()
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      const data = await adminService.getUsers(buildAdminUserListRequestOptions(page, limit, snapshotFilters(overrides)))

      if (data.success) {
        setUsers(data.users)
        setPagination((prev) => ({
          ...prev,
          page,
          limit,
          total: data.pagination.total
        }))
      } else {
        console.error('Failed to fetch users: Unknown error')
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setPagination((prev) => ({ ...prev, page: 1 }))
    void fetchPage(1, pagination.limit, { searchTerm: value })
  }

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([])
    } else {
      setSelectedUsers(users.map(user => user.id))
    }
  }

  const handleDeleteUser = async (user: User) => {
    setSelectedUserForDeletion(user)
    setDeletionReason('')
    setDeletionPreview(null)
    
    try {
      // Get current user session token
      const currentUser = Parse.User.current()
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      const sessionToken = currentUser.getSessionToken()
      if (!sessionToken) {
        throw new Error('No session token available')
      }

      // Get deletion preview
      const data = await adminService.getUserDeletionPreview(user.id)
      
      if (data.success) {
        setDeletionPreview(data)
      } else {
        console.error('Failed to get deletion preview:', data.error)
      }
    } catch (error) {
      console.error('Error getting deletion preview:', error)
    }
    
    setDeleteDialogOpen(true)
  }

  const handleConfirmDeletion = async () => {
    if (!selectedUserForDeletion) return
    
    setIsDeleting(true)
    
    try {
      // Get current user session token
      const currentUser = Parse.User.current()
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      const sessionToken = currentUser.getSessionToken()
      if (!sessionToken) {
        throw new Error('No session token available')
      }

      const data = await adminService.deleteUser(
        selectedUserForDeletion.id,
        deletionReason || 'User deletion requested by admin'
      )
      
      if (data.success) {
        alert(`User "${selectedUserForDeletion.username}" has been completely deleted from the platform.`)
        setDeleteDialogOpen(false)
        setSelectedUserForDeletion(null)
        setDeletionPreview(null)
        setDeletionReason('')
        // Refresh the users list
        void fetchPage(pagination.page, pagination.limit)
      } else {
        alert(`Error deleting user: ${data.error}`)
      }
    } catch (error) {
      console.error('Error deleting user:', error)
      alert(describeSingleDeleteFailure(error))
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setSelectedUserForDeletion(null)
    setDeletionPreview(null)
    setDeletionReason('')
    setIsDeleting(false)
  }

  const handleBulkDelete = () => {
    if (selectedUsers.length === 0) {
      alert('Please select users to delete')
      return
    }
    setBulkDeleteDialogOpen(true)
  }

  const handleConfirmBulkDeletion = async () => {
    if (selectedUsers.length === 0) return
    
    setIsDeleting(true)
    
    try {
      // Get current user session token
      const currentUser = Parse.User.current()
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      const sessionToken = currentUser.getSessionToken()
      if (!sessionToken) {
        throw new Error('No session token available')
      }

      // One refusal (a last Owner) must not hide the others' results, and the calls run in turn so
      // the server's last-Owner check sees each deletion.
      const outcomes = await deleteUsersSequentially(
        selectedUsers.map((userId) => ({
          userId,
          name: users.find((u) => u.id === userId)?.username ?? userId,
        })),
        (userId) =>
          adminService.deleteUser(userId, deletionReason || 'Bulk deletion requested by admin')
      )
      const problem = summarizeBulkDelete(outcomes)

      if (problem) {
        alert(problem)
        if (outcomes.some((o) => o.status === 'deleted')) {
          const left = outcomes.filter((o) => o.status !== 'deleted').map((o) => o.userId)
          setSelectedUsers(left)
          void fetchPage(pagination.page, pagination.limit)
        }
      } else {
        alert(`Successfully deleted ${selectedUsers.length} users from the platform.`)
        setSelectedUsers([])
        void fetchPage(pagination.page, pagination.limit)
      }
    } catch (error) {
      console.error('Error during bulk deletion:', error)
      alert('Failed to delete users. Please try again.')
    } finally {
      setIsDeleting(false)
      setBulkDeleteDialogOpen(false)
      setDeletionReason('')
    }
  }

  const handleCloseBulkDeleteDialog = () => {
    setBulkDeleteDialogOpen(false)
    setDeletionReason('')
    setIsDeleting(false)
  }


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  useEffect(() => {
    void fetchPage(1, 20)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, [])

  return (
    <Paper
      elevation={0}
      className="sevv-card"
      sx={{
        p: 3,
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        bgcolor: 'background.paper',
        transition: 'all 0.3s ease-in-out',
        boxShadow: (t) => t.shadows[1],
        '&:hover': {
          bgcolor: 'background.paper',
          borderColor: 'primary.main',
          boxShadow: (t) => t.shadows[4],
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
        pb: 2,
        borderBottom: 1,
        borderBottomColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: (t) =>
              `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.light} 100%)`,
            boxShadow: (t) => `0 4px 14px 0 ${alpha(t.palette.primary.main, 0.3)}`,
          }}>
            <PersonIcon sx={{ color: 'primary.contrastText', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              className="sevv-text-primary"
              sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                fontSize: '1.25rem',
                lineHeight: 1.25
              }}
            >
              User Management
            </Typography>
            <Typography 
              variant="body2" 
              className="sevv-text-secondary"
              sx={{ 
                color: 'text.secondary', 
                mt: 0.5,
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}
            >
              List, select, and completely delete users from the platform
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            className="sevv-btn sevv-btn-secondary"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
            disabled={selectedUsers.length === 0 || loading}
            sx={{
              minHeight: '44px',
              padding: '8px 16px',
              borderRadius: '8px',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: selectedUsers.length > 0 ? 'error.main' : 'text.disabled',
              borderColor: selectedUsers.length > 0 ? 'error.main' : 'divider',
              borderWidth: '2px',
              '&:hover': {
                backgroundColor: selectedUsers.length > 0
                  ? alpha(theme.palette.error.main, 0.1)
                  : alpha(theme.palette.primary.main, 0.1),
                borderColor: selectedUsers.length > 0 ? 'error.main' : 'primary.main',
                transform: 'translateY(-1px)',
                boxShadow: selectedUsers.length > 0
                  ? `0 4px 14px 0 ${alpha(theme.palette.error.main, 0.3)}`
                  : `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.3)}`,
              },
              '&:disabled': {
                color: 'text.disabled',
                borderColor: 'divider',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            Delete Selected ({selectedUsers.length})
          </Button>
          <Button
            variant="contained"
            className="sevv-btn sevv-btn-primary"
            startIcon={<RefreshIcon />}
            onClick={() => void fetchPage(pagination.page, pagination.limit)}
            disabled={loading}
            sx={{
              minHeight: '44px',
              padding: '8px 16px',
              borderRadius: '8px',
              fontWeight: 500,
              fontSize: '0.875rem',
              background: (t) =>
                `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.light} 100%)`,
              color: 'primary.contrastText !important',
              boxShadow: (t) => `0 4px 14px 0 ${alpha(t.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: (t) =>
                  `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 100%)`,
                boxShadow: (t) => `0 6px 20px 0 ${alpha(t.palette.primary.main, 0.4)}`,
                transform: 'translateY(-1px)'
              },
              '&:disabled': {
                background: (t) => alpha(t.palette.primary.main, 0.3),
                color: 'text.disabled !important',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Search and Controls */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 3, 
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <Box sx={{ 
          position: 'relative', 
          flex: 1, 
          minWidth: '300px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <TextField
            placeholder="Search username or email..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="sevv-input"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ 
                    color: 'text.secondary', 
                    fontSize: 20,
                    position: 'absolute',
                    left: '12px',
                    zIndex: 1
                  }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: '100%',
              '& .MuiInputLabel-root': { 
                color: 'text.secondary',
                marginLeft: '16px',
                fontSize: '0.875rem'
              },
              '& .MuiOutlinedInput-root': { 
                color: 'text.primary',
                paddingLeft: '40px',
                minHeight: '44px',
                borderRadius: '8px',
                fontSize: '0.875rem',
                '& .MuiInputBase-input': { 
                  color: 'text.primary',
                  paddingLeft: '12px',
                  '&::placeholder': {
                    color: 'text.disabled',
                    opacity: 1
                  }
                }
              },
              '& .MuiOutlinedInput-notchedOutline': { 
                borderColor: 'divider',
                borderWidth: '2px',
                '&:hover': { 
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                },
                '&.Mui-focused': { 
                  borderColor: 'primary.main',
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                }
              },
            }}
          />
        </Box>
        <FormControl sx={{ minWidth: 140 }}>
          <InputLabel 
            className="sevv-text-secondary"
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          >
            Per Page
          </InputLabel>
          <Select
            value={pagination.limit}
            onChange={(e) => {
              const newLimit = Number(e.target.value)
              setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }))
              void fetchPage(1, newLimit)
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: 'background.paper',
                  border: 1,
        borderColor: 'divider',
                  borderRadius: '8px',
                  boxShadow: (t) => t.shadows[4],
                  '& .MuiMenuItem-root': {
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    padding: '8px 16px',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                    '&.Mui-selected': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      color: 'primary.main',
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.3),
                      }
                    }
                  }
                }
              }
            }}
            sx={{ 
              color: 'text.primary',
              minHeight: '44px',
              borderRadius: '8px',
              fontSize: '0.875rem',
              '& .MuiSelect-select': {
                paddingLeft: '12px',
                paddingRight: '32px'
              },
              '& .MuiOutlinedInput-notchedOutline': { 
                borderColor: 'divider',
                borderWidth: '2px',
                '&:hover': { 
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                },
                '&.Mui-focused': { 
                  borderColor: 'primary.main',
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                }
              },
              '& .MuiSelect-icon': { 
                color: 'text.secondary',
                right: '12px'
              }
            }}
          >
            <MenuItem value={10}>10 per page</MenuItem>
            <MenuItem value={20}>20 per page</MenuItem>
            <MenuItem value={50}>50 per page</MenuItem>
            <MenuItem value={100}>100 per page</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel id="user-filter-role-label">Role</InputLabel>
          <Select
            labelId="user-filter-role-label"
            label="Role"
            value={roleFilter}
            onChange={(e) => {
              const v = e.target.value
              setRoleFilter(v)
              setPagination((p) => ({ ...p, page: 1 }))
              void fetchPage(1, pagination.limit, { roleFilter: v })
            }}
          >
            <MenuItem value="all">All roles</MenuItem>
            <MenuItem value="admin">admin</MenuItem>
            <MenuItem value="global_admin">global_admin</MenuItem>
            <MenuItem value="moderator">moderator</MenuItem>
            <MenuItem value="user">user</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 170 }} size="small">
          <InputLabel id="user-filter-pa-label">Platform admin</InputLabel>
          <Select
            labelId="user-filter-pa-label"
            label="Platform admin"
            value={platformAdminFilter}
            onChange={(e) => {
              const v = e.target.value as 'any' | 'yes' | 'no'
              setPlatformAdminFilter(v)
              setPagination((p) => ({ ...p, page: 1 }))
              void fetchPage(1, pagination.limit, { platformAdminFilter: v })
            }}
          >
            <MenuItem value="any">Any</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Created from"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={createdFrom}
          onChange={(e) => {
            const v = e.target.value
            setCreatedFrom(v)
            setPagination((p) => ({ ...p, page: 1 }))
            void fetchPage(1, pagination.limit, { createdFrom: v })
          }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="Created to"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={createdTo}
          onChange={(e) => {
            const v = e.target.value
            setCreatedTo(v)
            setPagination((p) => ({ ...p, page: 1 }))
            void fetchPage(1, pagination.limit, { createdTo: v })
          }}
          sx={{ minWidth: 160 }}
        />

        <Autocomplete
          size="small"
          sx={{ minWidth: 260 }}
          options={companyOptions}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={companyFilter}
          disabled={noCompany}
          onOpen={() => loadCompanies('')}
          onChange={(_, v) => {
            setCompanyFilter(v)
            setNoCompany(false)
            setPagination((p) => ({ ...p, page: 1 }))
            void fetchPage(1, pagination.limit, { companyFilter: v, noCompany: false })
          }}
          onInputChange={(_, v, reason) => {
            if (reason === 'input') loadCompanies(v)
          }}
          renderInput={(params) => (
            <TextField {...params} label="Company" placeholder="Search companies" />
          )}
        />

        <FormControlLabel
          sx={{ ml: 0, alignSelf: 'center' }}
          control={
            <Checkbox
              checked={noCompany}
              onChange={(_, c) => {
                setNoCompany(c)
                if (c) setCompanyFilter(null)
                setPagination((p) => ({ ...p, page: 1 }))
                void fetchPage(1, pagination.limit, {
                  noCompany: c,
                  ...(c ? { companyFilter: null } : {}),
                })
              }}
            />
          }
          label="No active company"
        />
      </Box>

      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          padding: 6,
          flexDirection: 'column',
          gap: 2
        }}>
          <CircularProgress 
            sx={{ 
              color: 'primary.main',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }} 
            size={40}
          />
          <Typography 
            variant="body2" 
            className="sevv-text-secondary"
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.875rem'
            }}
          >
            Loading users...
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer 
            component={Paper} 
            className="sevv-card"
            sx={{ 
              bgcolor: 'background.paper',
              border: 1,
        borderColor: 'divider',
              borderRadius: '12px',
              maxHeight: '60vh',
              overflow: 'auto',
              boxShadow: (t) => t.shadows[1],
              '&:hover': {
                borderColor: alpha(theme.palette.primary.main, 0.35),
                boxShadow: (t) => t.shadows[4]
              }
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  bgcolor: 'action.hover',
                  '& .MuiTableCell-root': {
                    borderBottom: 2,
                    borderBottomColor: 'divider'
                  }
                }}>
                  <TableCell sx={{ 
                    color: 'text.primary', 
                    fontWeight: 600, 
                    padding: '16px 12px', 
                    width: '40px',
                    fontSize: '0.875rem'
                  }}>
                    <Checkbox
                      checked={selectedUsers.length === users.length && users.length > 0}
                      indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                      onChange={handleSelectAll}
                      sx={{ 
                        color: 'text.secondary',
                        '&.Mui-checked': {
                          color: 'primary.main'
                        },
                        '&.MuiCheckbox-indeterminate': {
                          color: 'primary.main'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ 
                    color: 'text.primary', 
                    fontWeight: 600, 
                    padding: '16px 12px',
                    fontSize: '0.875rem'
                  }}>
                    User
                  </TableCell>
                  <TableCell sx={{ 
                    color: 'text.primary', 
                    fontWeight: 600, 
                    padding: '16px 12px',
                    fontSize: '0.875rem'
                  }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ 
                    color: 'text.primary', 
                    fontWeight: 600, 
                    padding: '16px 12px', 
                    width: '100px',
                    fontSize: '0.875rem'
                  }}>
                    Role
                  </TableCell>
                  <TableCell sx={{ 
                    color: 'text.primary', 
                    fontWeight: 600, 
                    padding: '16px 12px', 
                    width: '120px',
                    fontSize: '0.875rem'
                  }}>
                    Admin
                  </TableCell>
                  <TableCell sx={{ 
                    color: 'text.primary', 
                    fontWeight: 600, 
                    padding: '16px 12px', 
                    width: '100px',
                    fontSize: '0.875rem'
                  }}>
                    Created
                  </TableCell>
                  <TableCell sx={{ 
                    color: 'text.primary', 
                    fontWeight: 600, 
                    padding: '16px 12px', 
                    width: '80px',
                    fontSize: '0.875rem'
                  }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow 
                    key={user.id} 
                    sx={{ 
                      '&:hover': { 
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        transform: 'translateY(-1px)',
                        boxShadow: (t) => t.shadows[1]
                      },
                      transition: 'all 0.2s ease-in-out',
                      '&:nth-of-type(even)': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <TableCell sx={{ 
                      color: 'text.secondary', 
                      padding: '16px 12px',
                      borderBottom: 1,
                      borderBottomColor: 'divider'
                    }}>
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleSelectUser(user.id)}
                        sx={{ 
                          color: 'text.secondary',
                          '&.Mui-checked': {
                            color: 'primary.main'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ 
                      color: 'text.secondary', 
                      padding: '16px 12px',
                      borderBottom: 1,
                      borderBottomColor: 'divider'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ProfileAvatar userId={user.id} name={user.username} photoUrl={user.customAvatarUrl} patternStyle={user.avatar} size={32} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography 
                            variant="body2" 
                            className="sevv-text-primary"
                            sx={{ 
                              fontWeight: 500, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                              color: 'text.primary',
                              fontSize: '0.875rem'
                            }}
                          >
                            {user.username}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            className="sevv-text-secondary"
                            sx={{ 
                              fontSize: '0.75rem',
                              color: 'text.disabled'
                            }}
                          >
                            {user.id.slice(0, 8)}...
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      color: 'text.secondary', 
                      padding: '16px 12px',
                      borderBottom: 1,
                      borderBottomColor: 'divider'
                    }}>
                      <Typography 
                        variant="body2" 
                        className="sevv-text-secondary"
                        sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap', 
                          maxWidth: '200px',
                          fontSize: '0.875rem'
                        }}
                      >
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ 
                      color: 'text.secondary', 
                      padding: '16px 12px',
                      borderBottom: 1,
                      borderBottomColor: 'divider'
                    }}>
                      <Chip
                        label={user.role}
                        size="small"
                        sx={{
                          fontSize: '0.75rem',
                          height: '24px',
                          fontWeight: 500,
                          borderRadius: '12px',
                          ...(user.role === 'admin' && {
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            color: 'error.main',
                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                          }),
                          ...(user.role === 'moderator' && {
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: 'info.main',
                            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                          }),
                          ...(user.role === 'user' && {
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            color: 'success.main',
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                          }),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ 
                      color: 'text.secondary', 
                      padding: '16px 12px',
                      borderBottom: 1,
                      borderBottomColor: 'divider'
                    }}>
                      <Chip
                        label={user.isPlatformAdmin ? 'Yes' : 'No'}
                        size="small"
                        sx={{
                          fontSize: '0.75rem',
                          height: '24px',
                          fontWeight: 500,
                          borderRadius: '12px',
                          ...(user.isPlatformAdmin ? {
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            color: 'error.main',
                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                          } : {
                            bgcolor: alpha(theme.palette.text.secondary, 0.12),
                            color: 'text.secondary',
                            border: `1px solid ${alpha(theme.palette.text.secondary, 0.35)}`,
                          })
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ 
                      color: 'text.secondary', 
                      padding: '16px 12px',
                      borderBottom: 1,
                      borderBottomColor: 'divider'
                    }}>
                      <Typography 
                        variant="body2" 
                        className="sevv-text-secondary"
                        sx={{ 
                          fontSize: '0.875rem'
                        }}
                      >
                        {formatDate(user.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ 
                      color: 'text.secondary', 
                      padding: '16px 12px',
                      borderBottom: 1,
                      borderBottomColor: 'divider'
                    }}>
                      <Tooltip title="Delete user completely" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteUser(user)}
                          sx={{
                            color: 'text.secondary',
                            transition: 'all 0.2s ease-in-out',
                            padding: '8px',
                            borderRadius: '8px',
                            '&:hover': {
                              color: 'error.main',
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                              transform: 'scale(1.1)',
                              boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.3)}`
                            },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mt: 3, 
            pt: 2,
            borderTop: 1,
            borderTopColor: 'divider'
          }}>
            <Typography 
              variant="body2" 
              className="sevv-text-secondary"
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
            </Typography>
            <Pagination
              count={Math.ceil(pagination.total / pagination.limit)}
              page={pagination.page}
              onChange={(_, page) => {
                setPagination(prev => ({ ...prev, page }))
                void fetchPage(page, pagination.limit)
              }}
              color="primary"
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-root': {
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  minWidth: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    borderColor: 'primary.main',
                    transform: 'translateY(-1px)',
                    boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                },
                '& .MuiPaginationItem-root.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText !important',
                  borderColor: 'primary.main',
                  boxShadow: (t) => `0 4px 14px 0 ${alpha(t.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    transform: 'translateY(-1px)',
                    boxShadow: (t) => `0 6px 20px 0 ${alpha(t.palette.primary.main, 0.4)}`,
                  }
                },
                '& .MuiPaginationItem-ellipsis': {
                  color: 'text.secondary'
                }
              }}
            />
          </Box>
        </>
      )}

      {/* Delete User Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="lg"
        fullWidth
        className="sevv-dialog"
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            borderRadius: '16px',
            border: 1,
            borderColor: 'divider',
            boxShadow: (t) => t.shadows[12],
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'action.hover', 
          color: 'text.primary',
          borderBottom: 1,
          borderBottomColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          padding: '24px'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '12px',
            bgcolor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
          }}>
            <WarningIcon sx={{ color: 'error.main', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              className="sevv-text-primary"
              sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                fontSize: '1.25rem'
              }}
            >
              Delete User: {selectedUserForDeletion?.username}
            </Typography>
            <Typography 
              variant="body2" 
              className="sevv-text-secondary"
              sx={{ 
                color: 'text.secondary', 
                mt: 0.5,
                fontSize: '0.875rem'
              }}
            >
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          padding: '24px', 
          color: 'text.primary',
          maxHeight: '60vh',
          overflow: 'auto'
        }}>
          {deletionPreview && (
            <Box sx={{ mb: 3 }}>
              <Alert 
                severity="warning" 
                className="sevv-alert sevv-alert-warning"
                sx={{ 
                  mb: 2, 
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  borderRadius: '8px',
                  '& .MuiAlert-icon': {
                    color: 'warning.main'
                  }
                }}
              >
                <Typography 
                  variant="body2" 
                  className="sevv-text-primary"
                  sx={{ 
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  <strong>Warning:</strong> This action cannot be undone. The following will be permanently deleted:
                </Typography>
              </Alert>
              
              <Box sx={{ 
                mt: 3, 
                p: 2, 
                bgcolor: 'background.paper', 
                border: 1,
        borderColor: 'divider',
                borderRadius: 2,
                mb: 2
              }}>
                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>
                  Deletion Impact Summary
                </Typography>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, paddingLeft: '8px' }}>
                  {deletionPreview.summary.totalCompanyAssignments > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {deletionPreview.summary.totalCompanyAssignments} Company Assignment(s)
                      </Typography>
                    </Box>
                  )}
                  
                  {deletionPreview.summary.totalRooms > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RoomIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {deletionPreview.summary.totalRooms} Room(s)
                      </Typography>
                    </Box>
                  )}
                  
                  {deletionPreview.summary.totalProfiles > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {deletionPreview.summary.totalProfiles} Profile(s)
                      </Typography>
                    </Box>
                  )}
                  
                  {deletionPreview.summary.totalBillingAccounts > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalanceWalletIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {deletionPreview.summary.totalBillingAccounts} Billing Account(s)
                      </Typography>
                    </Box>
                  )}
                  
                  {deletionPreview.summary.totalSessions > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {deletionPreview.summary.totalSessions} Session(s)
                      </Typography>
                    </Box>
                  )}
                  
                  {deletionPreview.summary.totalMessages > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MessageIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {deletionPreview.summary.totalMessages} Message(s)
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              
              {deletionPreview.warnings.length > 0 && (
                <Alert severity="info" sx={{ bgcolor: alpha(theme.palette.info.main, 0.08), border: `1px solid ${alpha(theme.palette.info.main, 0.35)}` }}>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    <strong>Important Notes:</strong>
                  </Typography>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    {deletionPreview.warnings.map((warning, index) => (
                      <li key={index}>
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                          {warning}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}
            </Box>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            label="Reason for deletion (optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={deletionReason}
            onChange={(e) => setDeletionReason(e.target.value)}
            placeholder="Enter the reason for deleting this user..."
            InputProps={{
              startAdornment: <WarningIcon sx={{ color: 'text.secondary', mr: 2 }} />
            }}
            sx={{
              '& .MuiInputLabel-root': { 
                color: 'text.secondary',
                marginLeft: '16px'
              },
              '& .MuiOutlinedInput-root': { 
                color: 'text.primary',
                paddingLeft: '16px',
                '& .MuiInputBase-input': { 
                  color: 'text.primary',
                  paddingLeft: '12px'
                }
              },
              '& .MuiOutlinedInput-notchedOutline': { 
                borderColor: 'divider',
                '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.35) },
                '&.Mui-focused': { borderColor: 'primary.main' }
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          bgcolor: 'action.hover', 
          borderTop: 1,
          borderTopColor: 'divider',
          padding: '24px',
          gap: 2
        }}>
          <Button 
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            className="sevv-btn sevv-btn-secondary"
            disabled={isDeleting}
            sx={{ 
              minHeight: '44px',
              padding: '8px 16px',
              borderRadius: '8px',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'text.primary', 
              borderColor: 'divider',
              borderWidth: '2px',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                transform: 'translateY(-1px)',
                boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.2)}`,
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDeletion}
            variant="contained"
            className="sevv-btn sevv-btn-primary"
            disabled={isDeleting}
            sx={{ 
              minHeight: '44px',
              padding: '8px 16px',
              borderRadius: '8px',
              fontWeight: 500,
              fontSize: '0.875rem',
              bgcolor: 'error.main',
              color: 'error.contrastText !important',
              boxShadow: (t) => `0 4px 14px 0 ${alpha(t.palette.error.main, 0.35)}`,
              '&:hover': {
                bgcolor: 'error.dark',
                transform: 'translateY(-1px)',
                boxShadow: (t) => `0 6px 20px 0 ${alpha(t.palette.error.main, 0.45)}`,
              },
              '&:disabled': {
                bgcolor: alpha(theme.palette.error.main, 0.35),
                color: 'text.disabled !important',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete User Completely'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={handleCloseBulkDeleteDialog}
        maxWidth="md"
        fullWidth
        className="sevv-dialog"
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            borderRadius: '16px',
            border: 1,
            borderColor: 'divider',
            boxShadow: (t) => t.shadows[12],
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'action.hover', 
          color: 'text.primary',
          borderBottom: 1,
          borderBottomColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          padding: '24px'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '12px',
            bgcolor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
          }}>
            <WarningIcon sx={{ color: 'error.main', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              className="sevv-text-primary"
              sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                fontSize: '1.25rem'
              }}
            >
              Delete {selectedUsers.length} Selected Users
            </Typography>
            <Typography 
              variant="body2" 
              className="sevv-text-secondary"
              sx={{ 
                color: 'text.secondary', 
                mt: 0.5,
                fontSize: '0.875rem'
              }}
            >
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          padding: '24px', 
          color: 'text.primary',
          maxHeight: '60vh',
          overflow: 'auto'
        }}>
          <Alert 
            severity="warning" 
            className="sevv-alert sevv-alert-warning"
            sx={{ 
              mb: 2, 
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              borderRadius: '8px',
              '& .MuiAlert-icon': {
                color: 'warning.main'
              }
            }}
          >
            <Typography 
              variant="body2" 
              className="sevv-text-primary"
              sx={{ 
                color: 'text.primary',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              <strong>Warning:</strong> You are about to permanently delete {selectedUsers.length} users from the platform. This action cannot be undone and will remove all user data, company assignments, and related information.
            </Typography>
          </Alert>
          
          <TextField
            autoFocus
            margin="dense"
            label="Reason for bulk deletion (optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={deletionReason}
            onChange={(e) => setDeletionReason(e.target.value)}
            placeholder="Enter the reason for deleting these users..."
            className="sevv-input"
            InputProps={{
              startAdornment: <WarningIcon sx={{ color: 'text.secondary', mr: 2 }} />
            }}
            sx={{
              '& .MuiInputLabel-root': { 
                color: 'text.secondary',
                marginLeft: '16px',
                fontSize: '0.875rem'
              },
              '& .MuiOutlinedInput-root': { 
                color: 'text.primary',
                paddingLeft: '16px',
                borderRadius: '8px',
                '& .MuiInputBase-input': { 
                  color: 'text.primary',
                  paddingLeft: '12px',
                  fontSize: '0.875rem'
                }
              },
              '& .MuiOutlinedInput-notchedOutline': { 
                borderColor: 'divider',
                borderWidth: '2px',
                '&:hover': { 
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                },
                '&.Mui-focused': { 
                  borderColor: 'primary.main',
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                }
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          bgcolor: 'action.hover', 
          borderTop: 1,
          borderTopColor: 'divider',
          padding: '24px',
          gap: 2
        }}>
          <Button 
            onClick={handleCloseBulkDeleteDialog}
            variant="outlined"
            className="sevv-btn sevv-btn-secondary"
            disabled={isDeleting}
            sx={{ 
              minHeight: '44px',
              padding: '8px 16px',
              borderRadius: '8px',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'text.primary', 
              borderColor: 'divider',
              borderWidth: '2px',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                transform: 'translateY(-1px)',
                boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.2)}`,
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmBulkDeletion}
            variant="contained"
            className="sevv-btn sevv-btn-primary"
            disabled={isDeleting}
            sx={{ 
              minHeight: '44px',
              padding: '8px 16px',
              borderRadius: '8px',
              fontWeight: 500,
              fontSize: '0.875rem',
              bgcolor: 'error.main',
              color: 'error.contrastText !important',
              boxShadow: (t) => `0 4px 14px 0 ${alpha(t.palette.error.main, 0.35)}`,
              '&:hover': {
                bgcolor: 'error.dark',
                transform: 'translateY(-1px)',
                boxShadow: (t) => `0 6px 20px 0 ${alpha(t.palette.error.main, 0.45)}`,
              },
              '&:disabled': {
                bgcolor: alpha(theme.palette.error.main, 0.35),
                color: 'text.disabled !important',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : `Delete ${selectedUsers.length} Users`}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default UserManagementCard
